import React, { useState, useEffect } from 'react';
import { useAppSettings } from '../contexts/AppSettingsContext';
import useUnifiedAnalytics from '../hooks/useUnifiedAnalytics';
import './Analytics.css';

// ============ Constants & Helpers ============

const VIEW_MODES = [
  { id: 'daily', label: 'Daily' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'annual', label: 'Annual' },
];

const TABS = [
  { id: 'overview', label: 'Overview', icon: '\u{1F4CA}' },
  { id: 'sales', label: 'Sales', icon: '\u{1F4B0}' },
  { id: 'hourly', label: 'Hourly', icon: '\u{1F551}' },
  { id: 'items', label: 'Top Items', icon: '\u{2B50}' },
  { id: 'payments', label: 'Payments', icon: '\u{1F4B3}' },
  { id: 'labor', label: 'Labor', icon: '\u{1F552}' },
  { id: 'tips', label: 'Tips', icon: '\u{1F4B5}' },
  { id: 'servers', label: 'Servers', icon: '\u{1F37D}' },
  { id: 'inventory', label: 'Inventory', icon: '\u{1F4E6}' },
];

const fmt$ = (v) => `$${(v || 0).toFixed(2)}`;
const fmt$Compact = (v) => {
  // Scales for KPI tiles: < $100 → $12.34, < $10k → $1,234, ≥ $10k → $12.3k
  const n = v || 0;
  if (Math.abs(n) < 100) return `$${n.toFixed(2)}`;
  if (Math.abs(n) < 10000) return `$${Math.round(n).toLocaleString()}`;
  return `$${(n / 1000).toFixed(1)}k`;
};
const fmtN = (v) => (v || 0).toLocaleString();
const fmtHrs = (v) => (v || 0).toFixed(2);
const fmtPct = (v) => `${(v || 0).toFixed(1)}%`;
// Productivity-ratio formatting: covers/hr stays decimal at low values
// for resolution; revenue/hr leans on the compact dollar scale.
const fmtRatio = (v) => {
  if (!Number.isFinite(v) || v <= 0) return '—';
  if (v < 10) return v.toFixed(1);
  return Math.round(v).toLocaleString();
};
const fmtHour = (h) => { if (h === 0) return '12a'; if (h < 12) return `${h}a`; if (h === 12) return '12p'; return `${h - 12}p`; };
const fmtTime = (iso) => { if (!iso) return '--'; return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); };

const todayYMD = () => { const d = new Date(); return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`; };
const toISO = (ymd) => `${ymd.slice(0, 4)}-${ymd.slice(4, 6)}-${ymd.slice(6, 8)}`;
const fromISO = (s) => s.replace(/-/g, '');

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function getMonday(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
}

function getSunday(mondayYMD) {
  const d = new Date(toISO(mondayYMD) + 'T12:00:00');
  d.setDate(d.getDate() + 6);
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
}

function shiftWeek(mondayYMD, delta) {
  const d = new Date(toISO(mondayYMD) + 'T12:00:00');
  d.setDate(d.getDate() + delta * 7);
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
}

function shiftDay(ymd, delta) {
  const d = new Date(toISO(ymd) + 'T12:00:00');
  d.setDate(d.getDate() + delta);
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
}

// Friendly "Today" / "Yesterday" / "Mon, May 19" label for the day picker.
function formatDayLabel(ymd) {
  const d = new Date(toISO(ymd) + 'T12:00:00');
  const today = todayYMD();
  if (ymd === today) return 'Today';
  if (ymd === shiftDay(today, -1)) return 'Yesterday';
  if (ymd === shiftDay(today, 1)) return 'Tomorrow';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  const dayName = DAY_NAMES[(d.getDay() + 6) % 7]; // Mon-first
  const month = MONTH_NAMES[d.getMonth()];
  return sameYear
    ? `${dayName}, ${month} ${d.getDate()}`
    : `${dayName}, ${month} ${d.getDate()}, ${d.getFullYear()}`;
}

function formatDateRange(startYMD, endYMD) {
  const s = new Date(toISO(startYMD) + 'T12:00:00');
  const e = new Date(toISO(endYMD) + 'T12:00:00');
  return `${MONTH_NAMES[s.getMonth()]} ${s.getDate()} - ${MONTH_NAMES[e.getMonth()]} ${e.getDate()}, ${e.getFullYear()}`;
}

// ============ Pie Chart Components ============

const PIE_COLORS_WARM = ['#ff6b35', '#e85d26', '#d4a017', '#c45b28', '#f59e0b', '#ef4444', '#f97316', '#b45309', '#dc2626', '#ea580c'];
const PIE_COLORS_COOL = ['#6366f1', '#8b5cf6', '#3b82f6', '#06b6d4', '#0ea5e9', '#7c3aed', '#2563eb', '#0891b2', '#4f46e5', '#0284c7'];

const describeArc = (cx, cy, r, startAngle, endAngle) => {
  const sr = (startAngle - 90) * Math.PI / 180, er = (endAngle - 90) * Math.PI / 180;
  return `M ${cx} ${cy} L ${cx + r * Math.cos(sr)} ${cy + r * Math.sin(sr)} A ${r} ${r} 0 ${endAngle - startAngle > 180 ? 1 : 0} 1 ${cx + r * Math.cos(er)} ${cy + r * Math.sin(er)} Z`;
};

const DonutChart = ({ data, colors, total, centerLabel, centerValue, size = 200, onSliceClick, activeIndex }) => {
  const [hovered, setHovered] = useState(null);
  const cx = size / 2, cy = size / 2, outerR = size / 2 - 4, innerR = outerR * 0.58;
  let angle = 0;
  const slices = data.map((d) => { const a = total > 0 ? (d.revenue / total) * 360 : 0; const s = { start: angle, end: angle + Math.max(a, 0.5) }; angle += a; return s; });
  const di = hovered !== null ? hovered : activeIndex;
  return (
    <div className="donut-chart-wrapper">
      <svg width={size + 8} height={size + 8} viewBox={`-4 -4 ${size + 8} ${size + 8}`} className="donut-chart-svg">
        {slices.map((s, i) => (
          <path key={i} d={describeArc(cx, cy, hovered === i ? outerR + 4 : outerR, s.start, s.end)}
            fill={colors[i % colors.length]} opacity={di !== null && di !== i ? 0.35 : 1}
            style={{ cursor: 'pointer', transition: 'opacity 0.2s ease' }}
            onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}
            onClick={() => onSliceClick && onSliceClick(i)} />
        ))}
        <circle cx={cx} cy={cy} r={innerR} fill="#fff" />
        <text x={cx} y={cy - 6} textAnchor="middle" className="donut-center-value">{di !== null ? fmt$(data[di]?.revenue) : centerValue}</text>
        <text x={cx} y={cy + 12} textAnchor="middle" className="donut-center-label">{di !== null ? data[di]?.name || '' : centerLabel}</text>
      </svg>
    </div>
  );
};

const PieLegend = ({ data, colors, total, activeIndex, onItemClick, expandedIndex, showItems }) => {
  const [expandedItem, setExpandedItem] = useState(null);
  return (
    <div className="pie-legend">
      {data.map((d, i) => {
        const pct = total > 0 ? ((d.revenue / total) * 100).toFixed(1) : '0.0';
        const isExpanded = expandedIndex === i;
        return (
          <div key={d.name}>
            <div className={`pie-legend-item ${activeIndex === i ? 'active' : ''}`} onClick={() => onItemClick(i)}>
              <span className="pie-legend-swatch" style={{ background: colors[i % colors.length] }} />
              <div className="pie-legend-info">
                <span className="pie-legend-name">{d.name}</span>
                <span className="pie-legend-meta">{d.quantity !== undefined ? `${fmtN(d.quantity)} sold` : `${fmtN(d.count)} orders`}</span>
              </div>
              <div className="pie-legend-values">
                <span className="pie-legend-revenue">{fmt$(d.revenue)}</span>
                <span className="pie-legend-pct">{pct}%</span>
              </div>
              {showItems && d.items && <span className={`pie-legend-chevron ${isExpanded ? 'open' : ''}`}>{'\u25B6'}</span>}
            </div>
            {showItems && isExpanded && d.items && (
              <div className="pie-legend-drilldown">
                {d.items.map(item => {
                  const ik = `${i}-${item.name}`, isIE = expandedItem === ik;
                  return (
                    <div key={item.name}>
                      <div className={`pie-drilldown-item ${isIE ? 'item-active' : ''}`} onClick={(e) => { e.stopPropagation(); setExpandedItem(prev => prev === ik ? null : ik); }}>
                        <span className="pie-drilldown-name clickable">{item.name}{item.hourly?.length > 0 && <span className={`pie-drilldown-chevron ${isIE ? 'open' : ''}`}>{'\u25B6'}</span>}</span>
                        <span className="pie-drilldown-qty">{fmtN(item.quantity)}</span>
                        <span className="pie-drilldown-rev">{fmt$(item.revenue)}</span>
                        <span className="pie-drilldown-pct">{d.revenue > 0 ? ((item.revenue / d.revenue) * 100).toFixed(0) : 0}%</span>
                      </div>
                      {isIE && item.hourly?.length > 0 && <ItemHourlyChart hourly={item.hourly} />}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const ItemHourlyChart = ({ hourly }) => {
  const max = Math.max(...hourly.map(h => h.count), 1);
  return (
    <div className="item-hourly-chart">
      <div className="item-hourly-header">Orders by Hour</div>
      <div className="item-hourly-bars">
        {hourly.map(h => (
          <div key={h.hour} className="item-hourly-bar-wrapper">
            <span className="item-hourly-count">{h.count}</span>
            <div className="item-hourly-bar" style={{ height: `${(h.count / max) * 60}px` }} title={`${fmtHour(h.hour)}: ${h.count} sold`} />
            <span className="item-hourly-label">{fmtHour(h.hour)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============ Trend Bar Chart ============

const TrendChart = ({
  data, labelKey, valueKey = 'revenue', labelFn,
  color = '#ff6b35', secondaryKey, secondaryColor = '#6366f1',
  valueFmt = fmt$, secondaryFmt = fmtN,
}) => {
  const maxVal = Math.max(...data.map(d => d[valueKey]), 1);
  return (
    <div className="trend-chart">
      {data.map((d, i) => (
        <div key={i} className="trend-bar-col">
          <div className="trend-bar-values">
            <span className="trend-bar-amount">{valueFmt(d[valueKey])}</span>
            {secondaryKey && <span className="trend-bar-secondary">{secondaryFmt(d[secondaryKey])}</span>}
          </div>
          <div className="trend-bar-track">
            <div className="trend-bar-fill" style={{ height: `${(d[valueKey] / maxVal) * 100}%`, background: color }} />
          </div>
          <span className="trend-bar-label">{labelFn ? labelFn(d[labelKey], i) : d[labelKey]}</span>
        </div>
      ))}
    </div>
  );
};

// ============ Sales Tab Components (shared across views) ============

// Each themed panel: an eyebrow title, a hero number (large) with a unit
// + tone-colored caption, and a row of 2-4 secondary stats below a thin
// rule. Variants drive the color treatment.
const ClusterPanel = ({
  variant,        // 'revenue' | 'labor' | 'productivity' | 'tips' | 'attendance' | 'inventory'
  title,
  hero,           // { value, unit, caption, captionTone }
  stats,          // [{ label, value, sub, tone }]
  note,           // optional small note rendered at bottom
}) => (
  <article className={`cluster cluster--${variant}`}>
    <header className="cluster-head">
      <span className="cluster-title">{title}</span>
    </header>
    <div className="cluster-hero">
      <span className="cluster-hero-value">
        {hero.value}
        {hero.unit && <span className="cluster-hero-unit">{hero.unit}</span>}
      </span>
      {hero.caption && (
        <span className={`cluster-hero-caption ${hero.captionTone || ''}`}>{hero.caption}</span>
      )}
    </div>
    {stats && stats.length > 0 && (
      <ul className="cluster-stats">
        {stats.map((s, i) => (
          <li key={i} className={`cluster-stat ${s.tone || ''}`}>
            <span className="cluster-stat-label">{s.label}</span>
            <span className="cluster-stat-value">{s.value}</span>
            {s.sub && <span className="cluster-stat-sub">{s.sub}</span>}
          </li>
        ))}
      </ul>
    )}
    {note && <div className="cluster-note">{note}</div>}
  </article>
);

const OverviewCards = ({ data }) => {
  const t = data.totals || data.overview || {};
  const wageCoveragePct = Math.round((t.wageCoverage || 0) * 100);
  const wagesPartial = wageCoveragePct > 0 && wageCoveragePct < 99;
  const wagesMissing = (t.totalHours || 0) > 0 && (t.totalWages || 0) === 0;
  const att = data.attendance;
  const hasAttendance = att && att.hasSchedule;

  return (
    <div className="analytics-clusters">
      <ClusterPanel
        variant="revenue"
        title="Revenue"
        hero={{
          value: fmt$Compact(t.totalRevenue),
          caption: 'subtotal + tax · pre-tip',
        }}
        stats={[
          { label: 'Orders', value: fmtN(t.orderCount) },
          { label: 'Avg order', value: fmt$(t.avgOrderValue) },
          { label: 'Guests', value: fmtN(t.totalGuests) },
        ]}
      />

      <ClusterPanel
        variant="labor"
        title="Labor"
        hero={{
          value: fmt$Compact(t.totalWages),
          caption: wagesMissing
            ? 'no wage data resolved'
            : wagesPartial
              ? `${wageCoveragePct}% wage coverage`
              : 'labor cost',
          captionTone: (wagesMissing || wagesPartial) ? 'is-warn' : '',
        }}
        stats={[
          { label: 'Hours', value: fmtHrs(t.totalHours), sub: `${fmtHrs(t.totalRegularHours)}r + ${fmtHrs(t.totalOTHours)}OT` },
          {
            label: 'Labor %',
            value: fmtPct(t.laborCostPct),
            sub: 'of sales',
            tone: t.laborCostPct > 35 ? 'is-warn' : '',
          },
        ]}
      />

      <ClusterPanel
        variant="productivity"
        title="Productivity"
        hero={{
          value: fmtRatio(t.coversPerLaborHour),
          unit: 'covers/h',
          caption: `${fmtN(t.totalGuests)} covers ÷ ${fmtHrs(t.totalHours)} hrs`,
        }}
        stats={[
          {
            label: 'Revenue / lab-hr',
            value: fmt$Compact(t.revenuePerLaborHour),
            sub: `${fmt$Compact(t.totalRevenue)} ÷ hrs`,
          },
        ]}
      />

      <ClusterPanel
        variant="tips"
        title="Tips"
        hero={{
          value: fmt$Compact(t.totalTips),
          caption: `${fmtPct(t.overallTipPercent)} of sales`,
        }}
        stats={[
          { label: 'Tip rate', value: fmtPct(t.overallTipPercent) },
          { label: 'Tip pool', value: fmt$Compact(t.totalAllTips || t.totalTips) },
        ]}
      />

      {hasAttendance ? (
        <ClusterPanel
          variant="attendance"
          title="Attendance"
          hero={{
            value: att.adherence === null ? '—' : `${Math.round(att.adherence * 100)}%`,
            caption: 'of scheduled hours worked',
            captionTone: att.adherence !== null && att.adherence < 0.85 ? 'is-warn' : '',
          }}
          stats={[
            { label: 'Scheduled', value: `${fmtHrs(att.totalSched)}h` },
            { label: 'Actual', value: `${fmtHrs(att.totalActual)}h` },
            {
              label: 'No-shows',
              value: fmtN(att.noShows),
              tone: att.noShows > 0 ? 'is-bad' : '',
            },
            {
              label: 'Unsched',
              value: fmtN(att.unscheduled),
              tone: att.unscheduled > 0 ? 'is-warn' : '',
            },
          ]}
        />
      ) : (
        <ClusterPanel
          variant="attendance"
          title="Attendance"
          hero={{
            value: '—',
            caption: 'no scheduled shifts for this period',
          }}
        />
      )}

      <ClusterPanel
        variant="inventory"
        title="Inventory"
        hero={{
          value: fmtN(t.inventoryTracked || 0),
          caption: (t.inventoryTracked || 0) === 0
            ? 'everything in stock'
            : 'items 86’d or limited',
          captionTone: (t.inventoryOutOfStock || 0) > 0 ? 'is-bad' : '',
        }}
        stats={[
          {
            label: 'Out',
            value: fmtN(t.inventoryOutOfStock),
            tone: t.inventoryOutOfStock > 0 ? 'is-bad' : '',
          },
          {
            label: 'Limited',
            value: fmtN(t.inventoryLowStock),
            tone: t.inventoryLowStock > 0 ? 'is-warn' : '',
          },
          {
            label: 'Remaining',
            value: fmtN(t.inventoryRemaining),
            sub: 'units',
          },
        ]}
      />
    </div>
  );
};

const OverviewTab = ({ data, viewMode }) => (
  <>
    <OverviewCards data={data} />
    {viewMode === 'weekly' && data.dailyTrend?.length > 0 && (
      <div className="analytics-table-container">
        <h3 className="analytics-table-title">Daily Revenue This Week</h3>
        <TrendChart data={data.dailyTrend} labelKey="date" valueKey="revenue" secondaryKey="orders"
          labelFn={(d, i) => { const dt = new Date(toISO(d) + 'T12:00:00'); return `${DAY_NAMES[i] || MONTH_NAMES[dt.getMonth()]} ${dt.getDate()}`; }} />
      </div>
    )}
    {viewMode === 'annual' && data.monthlyTrend?.length > 0 && (
      <div className="analytics-table-container">
        <h3 className="analytics-table-title">Monthly Revenue</h3>
        <TrendChart data={data.monthlyTrend} labelKey="month" valueKey="revenue" secondaryKey="orders"
          labelFn={(m) => MONTH_NAMES[parseInt(m.slice(4)) - 1] || m} />
      </div>
    )}
    {viewMode === 'daily' && data.salesByHour?.length > 0 && (
      <div className="analytics-table-container">
        <h3 className="analytics-table-title">Sales by Hour</h3>
        <TrendChart data={data.salesByHour} labelKey="hour" valueKey="revenue" secondaryKey="guests"
          labelFn={(h) => fmtHour(h)} />
      </div>
    )}
  </>
);


// =================================================================
// Menu Explorer — category → item drill-down
//
// Revenue + units sold only. Cost/margin columns removed; Toast doesn't
// expose item-level cost on the standard menu payload, so we don't
// render fields we can't fill with real data.
// =================================================================
const MenuExplorerRow = ({ node, level, maxRevenue, expanded, onToggle }) => {
  const hasChildren = Array.isArray(node.items) && node.items.length > 0;
  const expandable = hasChildren;
  const sharePct = maxRevenue > 0 ? (node.revenue / maxRevenue) * 100 : 0;
  return (
    <li className={`menu-row level-${level} ${hasChildren ? 'has-children' : ''} ${expanded ? 'is-expanded' : ''}`}>
      <button
        type="button"
        className="menu-row-head"
        onClick={expandable ? onToggle : undefined}
        disabled={!expandable}
      >
        <span className="menu-row-chevron" aria-hidden="true">
          {expandable && (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
              style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s ease' }}>
              <path d="M9 18l6-6-6-6" />
            </svg>
          )}
        </span>
        <span className="menu-row-name">{node.name}</span>
        <span className="menu-row-qty">{fmtN(node.quantity)}<span className="u">×</span></span>
        <span className="menu-row-bar" aria-hidden="true">
          <span className="menu-row-bar-fill" style={{ width: `${sharePct}%` }} />
        </span>
        <span className="menu-row-revenue">{fmt$Compact(node.revenue)}</span>
      </button>
      {hasChildren && expanded && (
        <ul className="menu-children">
          {node.items.map(item => (
            <MenuExplorerRow
              key={item.name}
              node={item}
              level={level + 1}
              maxRevenue={maxRevenue}
              expanded={false}
              onToggle={() => {}}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

const MenuExplorer = ({ categories }) => {
  const [expandedIdx, setExpandedIdx] = useState(null);
  if (categories.length === 0) {
    return <div className="analytics-empty"><div className="analytics-empty-subtext">No sales for this period</div></div>;
  }
  const totalRevenue = categories.reduce((s, c) => s + c.revenue, 0);
  const totalQty = categories.reduce((s, c) => s + (c.quantity || 0), 0);
  const maxRevenue = Math.max(...categories.map(c => c.revenue), 1);
  return (
    <div className="menu-explorer">
      <div className="menu-explorer-head">
        <div className="menu-explorer-col menu-explorer-col--name">Item · click category to expand</div>
        <div className="menu-explorer-col menu-explorer-col--qty">Qty</div>
        <div className="menu-explorer-col menu-explorer-col--bar">Share of revenue</div>
        <div className="menu-explorer-col menu-explorer-col--num">Revenue</div>
      </div>
      <ul className="menu-list">
        {categories.map((cat, i) => (
          <MenuExplorerRow
            key={cat.name}
            node={cat}
            level={0}
            maxRevenue={maxRevenue}
            expanded={expandedIdx === i}
            onToggle={() => setExpandedIdx(p => p === i ? null : i)}
          />
        ))}
      </ul>
      <div className="menu-explorer-foot">
        <div className="menu-explorer-foot-name">Totals</div>
        <div className="menu-explorer-foot-stats">
          <span><strong>{fmtN(totalQty)}</strong> sold</span>
          <span className="menu-explorer-foot-sep">·</span>
          <span><strong>{fmt$(totalRevenue)}</strong> revenue</span>
        </div>
      </div>
    </div>
  );
};

const SalesTab = ({ data }) => {
  const { salesByCategory, salesByDiningOption } = data;
  const totalDin = salesByDiningOption.reduce((s, d) => s + d.revenue, 0);
  const [dinActive, setDinActive] = useState(null);
  return (
    <>
      <div className="analytics-table-container">
        <div className="analytics-section-head">
          <span className="analytics-section-eyebrow">Menu explorer</span>
          <span className="analytics-section-sub">Category → item · units sold + revenue</span>
        </div>
        <MenuExplorer categories={salesByCategory} />
      </div>
      <div className="analytics-table-container">
        <h3 className="analytics-table-title">Sales by Dining Option</h3>
        {salesByDiningOption.length > 0 ? (
          <div className="pie-chart-section">
            <DonutChart data={salesByDiningOption} colors={PIE_COLORS_COOL} total={totalDin} centerLabel="Revenue" centerValue={fmt$(totalDin)} activeIndex={dinActive} onSliceClick={(i) => setDinActive(p => p === i ? null : i)} />
            <PieLegend data={salesByDiningOption} colors={PIE_COLORS_COOL} total={totalDin} activeIndex={dinActive} onItemClick={(i) => setDinActive(p => p === i ? null : i)} />
          </div>
        ) : <div className="analytics-empty"><div className="analytics-empty-subtext">No data</div></div>}
      </div>
    </>
  );
};

const TopItemsTab = ({ data }) => (
  <div className="analytics-table-container">
    <h3 className="analytics-table-title">Top Selling Items</h3>
    {data.topItems.length === 0 ? <div className="analytics-empty"><div className="analytics-empty-subtext">No data</div></div> : (
      <table className="analytics-table"><thead><tr><th style={{ width: 40 }}>#</th><th>Item</th><th className="right">Qty</th><th className="right">Revenue</th></tr></thead>
        <tbody>{data.topItems.map((item, i) => (
          <tr key={item.name}><td><span className={`rank-badge ${i < 3 ? 'top-3' : ''}`}>{i + 1}</span></td><td>{item.name}</td><td className="right">{fmtN(item.quantity)}</td><td className="right currency">{fmt$(item.revenue)}</td></tr>
        ))}</tbody></table>
    )}
  </div>
);

const PaymentsTab = ({ data }) => (
  <div className="analytics-table-container">
    <h3 className="analytics-table-title">Payment Breakdown</h3>
    {data.paymentBreakdown.length === 0 ? <div className="analytics-empty"><div className="analytics-empty-subtext">No data</div></div> : (
      <table className="analytics-table"><thead><tr><th>Type</th><th className="right">Count</th><th className="right">Amount</th><th className="right">Tips</th></tr></thead>
        <tbody>{data.paymentBreakdown.map(p => (
          <tr key={p.type}><td>{p.type}</td><td className="right">{fmtN(p.count)}</td><td className="right currency">{fmt$(p.amount)}</td><td className="right currency">{fmt$(p.tips)}</td></tr>
        ))}</tbody></table>
    )}
  </div>
);

// ============ Hourly Tab ============

const HOURLY_METRICS = [
  { id: 'revenue', label: 'Revenue', color: '#ff6b35', fmt: fmt$Compact, fmtFull: fmt$ },
  { id: 'orders',  label: 'Orders',  color: '#6366f1', fmt: fmtN,        fmtFull: fmtN },
  { id: 'guests',  label: 'Covers',  color: '#0ea5e9', fmt: fmtN,        fmtFull: fmtN },
];

// 7-row × 24-col grid. Color saturation tracks the relative magnitude of
// the selected metric. Values render only on darker cells to avoid the
// "tiny grey number on light bg" anti-pattern. Empty cells stay nearly
// transparent so the grid stays readable even on sparse periods.
const HourlyHeatmap = ({ matrix, metric, color, fmt }) => {
  const max = Math.max(...matrix.map(c => c[metric] || 0), 1);
  const rows = [];
  for (let d = 0; d < 7; d++) {
    rows.push(
      matrix.filter(c => c.dow === d).sort((a, b) => a.hour - b.hour),
    );
  }
  return (
    <div className="hourly-heatmap">
      <div className="hourly-heatmap-grid">
        <div className="hourly-heatmap-corner" />
        {Array.from({ length: 24 }, (_, h) => (
          <div key={`hl-${h}`} className="hourly-heatmap-hour-label">{fmtHour(h)}</div>
        ))}
        {rows.map((row, d) => (
          <React.Fragment key={`row-${d}`}>
            <div className="hourly-heatmap-day-label">{DAY_NAMES[d]}</div>
            {row.map(cell => {
              const v = cell[metric] || 0;
              const intensity = max > 0 ? v / max : 0;
              const opacity = v === 0 ? 0.05 : 0.18 + 0.82 * intensity;
              return (
                <div
                  key={`c-${d}-${cell.hour}`}
                  className="hourly-heatmap-cell"
                  style={{ background: color, opacity }}
                  title={`${DAY_NAMES[d]} ${fmtHour(cell.hour)} · ${fmt(v)}`}
                >
                  {v > 0 && intensity > 0.45 && (
                    <span className="hourly-heatmap-cell-value">{fmt(v)}</span>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
      <div className="hourly-heatmap-legend">
        <span>Less</span>
        <span className="hourly-heatmap-legend-grad" style={{ background: `linear-gradient(to right, ${color}1a, ${color})` }} />
        <span>More</span>
      </div>
    </div>
  );
};

const HourlyTab = ({ data, viewMode }) => {
  const [metric, setMetric] = useState('revenue');
  const salesByHour = data.salesByHour || [];
  const matrix = data.hourlyByDayOfWeek || [];
  const dayCount = Math.max(1, data.dayCount || 1);
  const isMultiDay = viewMode !== 'daily';
  const m = HOURLY_METRICS.find(x => x.id === metric) || HOURLY_METRICS[0];

  if (salesByHour.length === 0) {
    return (
      <div className="analytics-empty">
        <div className="analytics-empty-icon">{'\u{1F551}'}</div>
        <div className="analytics-empty-text">No hourly data for this period</div>
        <div className="analytics-empty-subtext">Once orders are placed, the hour-by-hour breakdown appears here.</div>
      </div>
    );
  }

  const totals = {
    revenue: salesByHour.reduce((s, h) => s + (h.revenue || 0), 0),
    orders:  salesByHour.reduce((s, h) => s + (h.orders  || 0), 0),
    guests:  salesByHour.reduce((s, h) => s + (h.guests  || 0), 0),
  };
  const peak = salesByHour.reduce(
    (best, h) => ((h[metric] || 0) > (best ? (best[metric] || 0) : 0) ? h : best),
    null,
  );
  // Secondary metric on each bar — covers when looking at revenue/orders,
  // orders when looking at covers. The detail table below carries the
  // full triplet so the bar only has to surface one supporting number.
  const SECONDARY = { revenue: 'guests', orders: 'guests', guests: 'orders' };
  const secondaryKey = SECONDARY[metric];

  return (
    <>
      <div className="analytics-stats analytics-stats--hourly">
        <div className="stat-card stat-card--accent">
          <div className="stat-card-label">Revenue</div>
          <div className="stat-card-value currency">{fmt$Compact(totals.revenue)}</div>
          <div className="stat-card-sub">
            {isMultiDay ? `${fmt$Compact(totals.revenue / dayCount)} / day avg` : 'this day'}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Orders</div>
          <div className="stat-card-value">{fmtN(totals.orders)}</div>
          <div className="stat-card-sub">
            {isMultiDay ? `${fmtN(Math.round(totals.orders / dayCount))} / day avg` : 'this day'}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Covers</div>
          <div className="stat-card-value">{fmtN(totals.guests)}</div>
          <div className="stat-card-sub">
            {isMultiDay ? `${fmtN(Math.round(totals.guests / dayCount))} / day avg` : 'this day'}
          </div>
        </div>
        <div className="stat-card stat-card--accent">
          <div className="stat-card-label">Peak hour · {m.label.toLowerCase()}</div>
          <div className="stat-card-value">{peak ? fmtHour(peak.hour) : '—'}</div>
          <div className="stat-card-sub">{peak ? `${m.fmt(peak[metric])} ${m.label.toLowerCase()}` : 'no data'}</div>
        </div>
      </div>

      <div className="analytics-table-container">
        <div className="analytics-section-head hourly-section-head">
          <div className="analytics-section-head-text">
            <span className="analytics-section-eyebrow">By hour of day</span>
            <span className="analytics-section-sub">
              {isMultiDay
                ? `Aggregated across ${dayCount} ${dayCount === 1 ? 'day' : 'days'} · pick a metric`
                : 'Today · pick a metric to view'}
            </span>
          </div>
          <div className="hourly-metric-toggle" role="tablist" aria-label="Hourly metric">
            {HOURLY_METRICS.map(opt => (
              <button
                key={opt.id}
                type="button"
                role="tab"
                aria-selected={metric === opt.id}
                className={`hourly-metric-btn ${metric === opt.id ? 'active' : ''}`}
                style={metric === opt.id ? { background: opt.color, borderColor: opt.color } : undefined}
                onClick={() => setMetric(opt.id)}
              >{opt.label}</button>
            ))}
          </div>
        </div>
        <TrendChart
          data={salesByHour}
          labelKey="hour"
          valueKey={metric}
          color={m.color}
          valueFmt={m.fmt}
          secondaryKey={secondaryKey}
          secondaryFmt={fmtN}
          labelFn={(h) => fmtHour(h)}
        />
      </div>

      {isMultiDay && (
        <div className="analytics-table-container">
          <div className="analytics-section-head">
            <span className="analytics-section-eyebrow">Day of week × hour</span>
            <span className="analytics-section-sub">
              {m.label} intensity · darker = busier · totals across the period
            </span>
          </div>
          <HourlyHeatmap matrix={matrix} metric={metric} color={m.color} fmt={m.fmt} />
        </div>
      )}

      <div className="analytics-table-container">
        <div className="analytics-section-head">
          <span className="analytics-section-eyebrow">Hour-by-hour detail</span>
          <span className="analytics-section-sub">{salesByHour.length} active {salesByHour.length === 1 ? 'hour' : 'hours'}</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="analytics-table" style={{ minWidth: 600 }}>
            <thead>
              <tr>
                <th>Hour</th>
                <th className="right">Orders</th>
                <th className="right">Covers</th>
                <th className="right">Party size</th>
                <th className="right">Revenue</th>
                <th className="right">Avg check</th>
                {isMultiDay && <th className="right">Orders / day</th>}
                {isMultiDay && <th className="right">Covers / day</th>}
              </tr>
            </thead>
            <tbody>
              {salesByHour.map(h => {
                const avgCheck = h.orders > 0 ? h.revenue / h.orders : 0;
                const partySize = h.orders > 0 ? h.guests / h.orders : 0;
                return (
                  <tr key={h.hour}>
                    <td style={{ fontWeight: 600 }}>{fmtHour(h.hour)}</td>
                    <td className="right">{fmtN(h.orders)}</td>
                    <td className="right">{fmtN(h.guests)}</td>
                    <td className="right">{partySize > 0 ? partySize.toFixed(1) : '—'}</td>
                    <td className="right currency">{fmt$(h.revenue)}</td>
                    <td className="right">{fmt$(avgCheck)}</td>
                    {isMultiDay && <td className="right">{(h.orders / dayCount).toFixed(1)}</td>}
                    {isMultiDay && <td className="right">{(h.guests / dayCount).toFixed(1)}</td>}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="totals-row">
                <td>Totals</td>
                <td className="right">{fmtN(totals.orders)}</td>
                <td className="right">{fmtN(totals.guests)}</td>
                <td className="right">{totals.orders > 0 ? (totals.guests / totals.orders).toFixed(1) : '—'}</td>
                <td className="right currency">{fmt$(totals.revenue)}</td>
                <td className="right">{fmt$(totals.orders > 0 ? totals.revenue / totals.orders : 0)}</td>
                {isMultiDay && <td className="right">{(totals.orders / dayCount).toFixed(1)}</td>}
                {isMultiDay && <td className="right">{(totals.guests / dayCount).toFixed(1)}</td>}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </>
  );
};

// ============ Labor/Tip Tab Components ============

// ============ Labor charts ============

// Editorial palette for role coding — same conventions as the team-page
// labor audit. Largest-hours role gets the brand orange after sorting.
const ROLE_PALETTE = [
  '#ff6b35', '#ff8c5a', '#2b3a4b', '#7d8a99',
  '#ffd1b3', '#5b6b7c', '#ffa570', '#3d4f63',
];

const HOUR_BINS = 48; // half-hour slots
const HOUR_TICKS = [0, 6, 9, 12, 15, 18, 21, 24];
const hourLabel = (h) => h === 0 || h === 24 ? '12a'
  : h === 12 ? '12p' : h < 12 ? `${h}a` : `${h - 12}p`;

// Aggregate employeeHours rows into per-role rollups (hours + cost +
// people + half-hour density). Returns roles sorted by hours, color
// re-assigned post-sort so the dominant role anchors the palette.
function aggregateRoles(employeeHours) {
  const byRole = new Map();
  for (const eh of employeeHours) {
    const key = eh.jobTitle || 'Unknown';
    if (!byRole.has(key)) {
      byRole.set(key, {
        name: key,
        hours: 0,
        cost: 0,
        people: new Set(),
        slots: new Array(HOUR_BINS).fill(0),
        entries: 0,
      });
    }
    const r = byRole.get(key);
    r.hours += eh.totalHours || 0;
    r.cost += eh.totalWage || 0;
    if (eh.employeeGuid) r.people.add(eh.employeeGuid);
    r.entries += 1;

    // Fill the swimlane density bins for the entry's start->end window
    // within the same day. Multi-day entries don't apply on daily-view
    // analytics anyway (Toast scopes to businessDate).
    if (eh.clockIn) {
      const s = new Date(eh.clockIn);
      const e = eh.clockOut ? new Date(eh.clockOut) : new Date();
      if (!isNaN(s) && !isNaN(e) && e > s) {
        const sm = s.getHours() * 60 + s.getMinutes();
        const em = e.getHours() * 60 + e.getMinutes() + (e.getDate() !== s.getDate() ? 24 * 60 : 0);
        const sSlot = Math.floor(sm / 30);
        const eSlot = Math.min(HOUR_BINS, Math.ceil(em / 30));
        for (let i = Math.max(0, sSlot); i < eSlot; i++) r.slots[i] += 1;
      }
    }
  }
  const arr = Array.from(byRole.values())
    .map(r => ({ ...r, peopleCount: r.people.size }))
    .sort((a, b) => b.hours - a.hours)
    .map((r, i) => ({ ...r, color: ROLE_PALETTE[i % ROLE_PALETTE.length] }));
  return arr;
}

// Concurrent-staffing curve across all roles — drives the "ALL" line at
// the top of the swimlane and the y-axis scale.
function overallCoverage(roles) {
  const overall = new Array(HOUR_BINS).fill(0);
  for (const r of roles) {
    for (let i = 0; i < HOUR_BINS; i++) overall[i] += r.slots[i];
  }
  let peak = 0, peakSlot = 0;
  for (let i = 0; i < HOUR_BINS; i++) {
    if (overall[i] > peak) { peak = overall[i]; peakSlot = i; }
  }
  return { overall, peak, peakSlot };
}

// Group every clock-in/clock-out entry by employee, attach the role color
// derived from the same sort order aggregateRoles uses so the Gantt-style
// punch chart and the swimlane stay color-consistent. Multi-day spans get
// clamped to the businessDate window (Toast scopes daily anyway).
function groupShiftsByEmployee(employeeHours) {
  // Build the same role → color map aggregateRoles derives.
  const roleHours = new Map();
  for (const eh of employeeHours || []) {
    const key = (eh.jobTitle || 'Unknown').trim() || 'Unknown';
    roleHours.set(key, (roleHours.get(key) ?? 0) + (eh.totalHours || 0));
  }
  const sortedRoles = Array.from(roleHours.entries()).sort((a, b) => b[1] - a[1]);
  const roleColor = new Map(sortedRoles.map(([name], i) => [name, ROLE_PALETTE[i % ROLE_PALETTE.length]]));

  const byEmp = new Map();
  for (const eh of employeeHours || []) {
    if (!eh.clockIn) continue;
    const inDate = new Date(eh.clockIn);
    if (isNaN(inDate)) continue;
    const outDate = eh.clockOut ? new Date(eh.clockOut) : new Date();
    if (isNaN(outDate) || outDate <= inDate) continue;

    // Convert to minutes-from-midnight relative to the in-day. End may
    // cross midnight; clamp to 24h (1440) so the bar terminates at the
    // axis right edge instead of overshooting into a new chart row.
    const startMin = inDate.getHours() * 60 + inDate.getMinutes();
    const sameDay = outDate.toDateString() === inDate.toDateString();
    const rawEndMin = outDate.getHours() * 60 + outDate.getMinutes() + (sameDay ? 0 : 24 * 60);
    const endMin = Math.min(24 * 60, rawEndMin);

    const guid = eh.employeeGuid || eh.employeeName || `${startMin}-${endMin}`;
    if (!byEmp.has(guid)) {
      byEmp.set(guid, {
        guid,
        name: eh.employeeName || 'Unknown',
        primaryRole: eh.jobTitle || 'Unknown',
        roleColor: roleColor.get((eh.jobTitle || 'Unknown').trim() || 'Unknown') ?? ROLE_PALETTE[0],
        totalHours: 0,
        shifts: [],
      });
    }
    const e = byEmp.get(guid);
    e.totalHours += eh.totalHours || 0;
    e.shifts.push({
      startMin,
      endMin,
      role: eh.jobTitle || 'Unknown',
      color: roleColor.get((eh.jobTitle || 'Unknown').trim() || 'Unknown') ?? e.roleColor,
      active: !eh.clockOut,
      regularHours: eh.regularHours || 0,
      overtimeHours: eh.overtimeHours || 0,
      totalHours: eh.totalHours || 0,
      clockIn: eh.clockIn,
      clockOut: eh.clockOut,
      crossesMidnight: !sameDay,
    });
  }

  return Array.from(byEmp.values())
    .map((e) => ({
      ...e,
      shifts: e.shifts.sort((a, b) => a.startMin - b.startMin),
      earliestStart: Math.min(...e.shifts.map((s) => s.startMin)),
    }))
    .sort((a, b) => a.earliestStart - b.earliestStart || b.totalHours - a.totalHours);
}

const PunchTimeline = ({ employees }) => {
  const padLeft = 130;
  const padRight = 60;
  const padTop = 28;
  const rowH = 22;
  const rowGap = 4;
  const vbW = 880;
  const dayW = vbW - padLeft - padRight;
  const minuteW = dayW / (24 * 60);
  const height = padTop + Math.max(1, employees.length) * (rowH + rowGap) + 6;

  if (employees.length === 0) {
    return <div className="analytics-empty"><div className="analytics-empty-subtext">No punch data for this period</div></div>;
  }

  return (
    <svg viewBox={`0 0 ${vbW} ${height}`} width="100%" preserveAspectRatio="xMidYMid meet" role="img"
      aria-label="Punch times by employee" className="analytics-svg">
      {/* Axis ticks */}
      {HOUR_TICKS.map((h, i) => {
        const x = padLeft + (h / 24) * dayW;
        return (
          <g key={i}>
            <line x1={x} x2={x} y1={padTop - 6} y2={height - 4} stroke="#f0e4d8" strokeWidth="1"
              strokeDasharray={h === 0 || h === 24 ? null : '2 3'} />
            <text x={x} y={padTop - 14} textAnchor="middle" fontSize="10" fill="#9a8c7c"
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace" letterSpacing="0.05em">
              {hourLabel(h)}
            </text>
          </g>
        );
      })}

      {/* Employee rows */}
      {employees.map((e, ri) => {
        const y = padTop + ri * (rowH + rowGap);
        const name = e.name.length > 16 ? e.name.slice(0, 16) + '…' : e.name;
        return (
          <g key={e.guid}>
            <rect x={padLeft} y={y} width={dayW} height={rowH} fill="#fffdfb" />
            <rect x={padLeft - 6} y={y} width="4" height={rowH} fill={e.roleColor} rx="1.5" />
            <text x={padLeft - 14} y={y + rowH / 2 + 4} textAnchor="end" fontSize="11.5" fontWeight="600" fill="#1a1a1a">
              {name}
            </text>

            {e.shifts.map((s, si) => {
              const x = padLeft + s.startMin * minuteW;
              const w = Math.max(2, (s.endMin - s.startMin) * minuteW);
              const inTime = fmtTime(s.clockIn);
              const outTime = s.clockOut ? fmtTime(s.clockOut) : 'Active';
              const title = `${e.name} · ${s.role}\n${inTime} → ${outTime}\n${fmtHrs(s.totalHours)}h${s.overtimeHours > 0 ? ` (${fmtHrs(s.overtimeHours)} OT)` : ''}`;
              return (
                <g key={si}>
                  <rect
                    x={x}
                    y={y + 3}
                    width={w}
                    height={rowH - 6}
                    fill={s.color}
                    fillOpacity={s.active ? 0.55 : 0.85}
                    rx="3"
                    stroke={s.active ? s.color : 'none'}
                    strokeWidth={s.active ? 1.5 : 0}
                    strokeDasharray={s.active ? '3 2' : null}
                  >
                    <title>{title}</title>
                  </rect>
                  {s.overtimeHours > 0 && (
                    // OT marker — a small dark cap on the right edge
                    <rect x={x + w - 3} y={y + 3} width="3" height={rowH - 6} fill="#3d2818" fillOpacity="0.8" rx="1.5">
                      <title>{`${fmtHrs(s.overtimeHours)}h OT`}</title>
                    </rect>
                  )}
                  {w > 56 && (
                    <text x={x + 6} y={y + rowH / 2 + 3.5} fontSize="9.5" fontWeight="700" fill="#fff"
                      fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace" letterSpacing="0.04em">
                      {inTime}
                    </text>
                  )}
                </g>
              );
            })}

            <text x={padLeft + dayW + 8} y={y + rowH / 2 + 4} fontSize="10" fontWeight="700" fill="#6b5848"
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace">{fmtHrs(e.totalHours)}h</text>
          </g>
        );
      })}
    </svg>
  );
};

const SwimlaneChart = ({ roles, overall, peak }) => {
  const padLeft = 100;
  const padRight = 24;
  const padTop = 32;
  const overallH = 36;
  const rowH = 26;
  const rowGap = 5;
  const vbW = 880;
  const slotsW = vbW - padLeft - padRight;
  const slotW = slotsW / HOUR_BINS;
  const height = padTop + overallH + 10 + Math.max(1, roles.length) * (rowH + rowGap);
  const overallMax = Math.max(1, peak);

  return (
    <svg viewBox={`0 0 ${vbW} ${height}`} width="100%" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Hourly coverage by role" className="analytics-svg">
      {HOUR_TICKS.map((h, i) => {
        const x = padLeft + (h / 24) * slotsW;
        return (
          <g key={i}>
            <line x1={x} x2={x} y1={padTop - 6} y2={height - 4} stroke="#f0e4d8" strokeWidth="1"
              strokeDasharray={h === 0 || h === 24 ? null : '2 3'} />
            <text x={x} y={padTop - 14} textAnchor="middle" fontSize="10" fill="#9a8c7c"
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace" letterSpacing="0.05em">
              {hourLabel(h)}
            </text>
          </g>
        );
      })}
      <g>
        <text x={padLeft - 12} y={padTop + overallH / 2 + 4} textAnchor="end" fontSize="10" fill="#3d2818"
          letterSpacing="0.14em" fontWeight="800">ALL</text>
        {overall.map((v, i) => {
          if (v === 0) return null;
          const x = padLeft + i * slotW;
          const h = (v / overallMax) * overallH;
          return <rect key={i} x={x} y={padTop + overallH - h} width={slotW - 0.6} height={h} fill="#1a1a1a" fillOpacity="0.82" />;
        })}
        <text x={padLeft + slotsW + 8} y={padTop + overallH / 2 + 4} fontSize="10" fontWeight="800" fill="#1a1a1a"
          fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace">{peak}</text>
      </g>
      {roles.map((r, ri) => {
        const y = padTop + overallH + 10 + ri * (rowH + rowGap);
        const roleMax = Math.max(1, ...r.slots);
        return (
          <g key={r.name}>
            <rect x={padLeft} y={y} width={slotsW} height={rowH} fill="#fffdfb" />
            <rect x={padLeft - 6} y={y} width="4" height={rowH} fill={r.color} rx="1.5" />
            <text x={padLeft - 14} y={y + rowH / 2 + 4} textAnchor="end" fontSize="11.5" fontWeight="600" fill="#1a1a1a">
              {r.name.length > 14 ? r.name.slice(0, 14) + '…' : r.name}
            </text>
            {r.slots.map((v, i) => {
              if (v === 0) return null;
              const x = padLeft + i * slotW;
              const opacity = 0.28 + 0.72 * Math.min(1, v / roleMax);
              return <rect key={i} x={x} y={y + 2} width={slotW - 0.4} height={rowH - 4} fill={r.color} fillOpacity={opacity} />;
            })}
            <text x={padLeft + slotsW + 8} y={y + rowH / 2 + 4} fontSize="10" fontWeight="700" fill="#6b5848"
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace">{Math.max(...r.slots)}</text>
          </g>
        );
      })}
    </svg>
  );
};

// Horizontal role rollup — bar = % of period hours, with cost lockup
// to the right. Click is a no-op here (no drill-down yet in analytics).
const RoleRollup = ({ roles, maxHours, totalCost }) => (
  <ul className="analytics-rollup">
    {roles.map(r => {
      const pct = (r.hours / maxHours) * 100;
      const costPct = totalCost > 0 ? (r.cost / totalCost) * 100 : 0;
      return (
        <li key={r.name} className="analytics-rollup-row">
          <span className="analytics-rollup-stripe" style={{ background: r.color }} aria-hidden="true" />
          <span className="analytics-rollup-name">{r.name}</span>
          <span className="analytics-rollup-bar" aria-hidden="true">
            <span className="analytics-rollup-bar-fill" style={{ width: `${pct}%`, background: r.color }} />
          </span>
          <span className="analytics-rollup-hrs">
            <strong>{fmtHrs(r.hours)}</strong><span className="u">h</span>
          </span>
          <span className="analytics-rollup-cost">{fmt$Compact(r.cost)}</span>
          <span className="analytics-rollup-meta">
            {r.peopleCount} {r.peopleCount === 1 ? 'person' : 'people'} · {costPct.toFixed(0)}% of cost
          </span>
        </li>
      );
    })}
  </ul>
);

// Top earners list — drives an at-a-glance read of who's racking up
// the most hours/cost. Sorted by cost; bar relative to highest earner.
const TopEarners = ({ employeeHours }) => {
  const byPerson = new Map();
  for (const eh of employeeHours) {
    const k = eh.employeeGuid || eh.employeeName;
    if (!byPerson.has(k)) {
      byPerson.set(k, { name: eh.employeeName, role: eh.jobTitle, hours: 0, cost: 0, tipped: eh.tpiEnabled });
    }
    const b = byPerson.get(k);
    b.hours += eh.totalHours || 0;
    b.cost += eh.totalWage || 0;
  }
  const top = Array.from(byPerson.values()).sort((a, b) => b.cost - a.cost).slice(0, 8);
  if (top.length === 0) return null;
  const maxCost = Math.max(1, ...top.map(p => p.cost));
  return (
    <ol className="analytics-rank">
      {top.map((p, i) => {
        const pct = (p.cost / maxCost) * 100;
        return (
          <li key={i} className="analytics-rank-row">
            <span className="analytics-rank-pos">{String(i + 1).padStart(2, '0')}</span>
            <span className="analytics-rank-name">
              <span className="analytics-rank-name-main">{p.name}</span>
              <span className="analytics-rank-name-role">{p.role}</span>
            </span>
            <span className="analytics-rank-bar" aria-hidden="true">
              <span className="analytics-rank-bar-fill" style={{ width: `${pct}%` }} />
            </span>
            <span className="analytics-rank-hrs">{fmtHrs(p.hours)}<span className="u">h</span></span>
            <span className="analytics-rank-cost">{fmt$Compact(p.cost)}</span>
          </li>
        );
      })}
    </ol>
  );
};

// REG / OT split — a quick "are we paying premium time?" read. Shows
// the two slices side-by-side as a single horizontal bar plus the
// dollar premium burned on OT.
const OTSplit = ({ regHours, otHours, totalWages, employeeHours }) => {
  const total = regHours + otHours;
  if (total === 0) return null;
  const regPct = (regHours / total) * 100;
  // Estimate OT premium dollars: 0.5 × avg rate × ot hours (since OT
  // pay is 1.5x but REG portion is already in totalWages).
  const avgRate = totalWages > 0 && total > 0 ? totalWages / total : 0;
  const otPremium = avgRate * otHours * 0.333; // approx half-premium
  const empsWithOT = employeeHours.filter(e => (e.overtimeHours || 0) > 0).length;
  return (
    <div className="analytics-ot-split">
      <div className="analytics-ot-split-bar" aria-hidden="true">
        <span className="analytics-ot-split-fill is-reg" style={{ width: `${regPct}%` }} />
        <span className="analytics-ot-split-fill is-ot" style={{ width: `${100 - regPct}%` }} />
      </div>
      <div className="analytics-ot-split-legend">
        <span className="analytics-ot-split-key">
          <span className="analytics-ot-split-swatch is-reg" />
          <strong>{fmtHrs(regHours)}</strong>h regular
        </span>
        <span className="analytics-ot-split-key">
          <span className="analytics-ot-split-swatch is-ot" />
          <strong>{fmtHrs(otHours)}</strong>h OT · {empsWithOT} {empsWithOT === 1 ? 'employee' : 'employees'}
        </span>
        {otPremium > 0 && (
          <span className="analytics-ot-split-premium">
            ≈ {fmt$Compact(otPremium)} OT premium
          </span>
        )}
      </div>
    </div>
  );
};

// Paired horizontal bars per role: scheduled (dashed/light) vs actual
// (solid). Single shared scale across all rows.
const AttendanceRoleChart = ({ roles }) => {
  const max = Math.max(1, ...roles.flatMap(r => [r.schedHours, r.actualHours]));
  return (
    <ul className="analytics-attendance-list">
      {roles.map((r, i) => {
        const sPct = (r.schedHours / max) * 100;
        const aPct = (r.actualHours / max) * 100;
        const color = ROLE_PALETTE[i % ROLE_PALETTE.length];
        const adh = r.adherence === null ? '—' : `${Math.round(r.adherence * 100)}%`;
        const deltaSign = r.delta > 0 ? '+' : r.delta < 0 ? '−' : '';
        const deltaClass = r.delta > 0.5 ? 'is-up' : r.delta < -0.5 ? 'is-down' : 'is-flat';
        return (
          <li key={r.name} className="analytics-attendance-row">
            <span className="analytics-attendance-stripe" style={{ background: color }} aria-hidden="true" />
            <span className="analytics-attendance-name">{r.name}</span>
            <div className="analytics-attendance-pair">
              <div className="analytics-attendance-pair-row">
                <span className="analytics-attendance-tag analytics-attendance-tag--sched">S</span>
                <span className="analytics-attendance-bar"><span className="analytics-attendance-bar-fill is-sched" style={{ width: `${sPct}%`, background: color }} /></span>
                <span className="analytics-attendance-num"><strong>{fmtHrs(r.schedHours)}</strong>h</span>
              </div>
              <div className="analytics-attendance-pair-row">
                <span className="analytics-attendance-tag analytics-attendance-tag--actual">A</span>
                <span className="analytics-attendance-bar"><span className="analytics-attendance-bar-fill is-actual" style={{ width: `${aPct}%`, background: color }} /></span>
                <span className="analytics-attendance-num"><strong>{fmtHrs(r.actualHours)}</strong>h</span>
              </div>
            </div>
            <div className="analytics-attendance-meta">
              <span className="analytics-attendance-adherence">{adh}</span>
              <span className={`analytics-attendance-delta ${deltaClass}`}>
                {deltaSign}{fmtHrs(Math.abs(r.delta))}h
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

const AttendanceVariance = ({ employees }) => {
  // Anyone with a non-trivial variance, sorted worst-first
  const flagged = employees.filter(e =>
    e.status === 'no_show' || e.status === 'unscheduled' ||
    (e.status === 'variance' && Math.abs(e.delta) >= 0.5)
  );
  if (flagged.length === 0) {
    return (
      <div className="analytics-empty">
        <div className="analytics-empty-icon">{'\u{2713}'}</div>
        <div className="analytics-empty-text">No attendance flags</div>
        <div className="analytics-empty-subtext">Everyone on the schedule clocked the hours they were booked for.</div>
      </div>
    );
  }
  return (
    <ul className="analytics-flag-list">
      {flagged.map((e, i) => {
        const sign = e.delta > 0 ? '+' : '−';
        const deltaTxt = Math.abs(e.delta) >= 0.05 ? `${sign}${fmtHrs(Math.abs(e.delta))}h` : '0h';
        const pctTxt = e.deltaPct !== null && Math.abs(e.deltaPct) >= 1
          ? ` · ${e.deltaPct > 0 ? '+' : ''}${Math.round(e.deltaPct)}%`
          : '';
        const statusLabel = e.status === 'no_show' ? 'No-show'
          : e.status === 'unscheduled' ? 'Unscheduled'
          : e.delta > 0 ? 'Over' : 'Under';
        return (
          <li key={i} className={`analytics-flag-row is-${e.status}`}>
            <span className="analytics-flag-tag">{statusLabel}</span>
            <span className="analytics-flag-name">
              <span className="analytics-flag-name-main">{e.employeeName}</span>
              <span className="analytics-flag-name-role">{e.roleName}</span>
            </span>
            <span className="analytics-flag-stats">
              <span><strong>{fmtHrs(e.schedHours)}</strong>h scheduled</span>
              <span className="analytics-flag-sep">→</span>
              <span><strong>{fmtHrs(e.actualHours)}</strong>h actual</span>
            </span>
            <span className="analytics-flag-delta">{deltaTxt}{pctTxt}</span>
          </li>
        );
      })}
    </ul>
  );
};

const LaborTab = ({ data }) => {
  const { employeeHours, laborTotals: t } = data;
  const roles = aggregateRoles(employeeHours);
  const { overall, peak, peakSlot } = overallCoverage(roles);
  const maxHours = Math.max(1, ...roles.map(r => r.hours));

  const peakHourLabel = (() => {
    if (peak === 0) return '—';
    const m = peakSlot * 30;
    const h = Math.floor(m / 60);
    const half = m % 60 === 0 ? '00' : '30';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${half}${h < 12 ? 'a' : 'p'}`;
  })();

  const wageCoveragePct = Math.round((t.wageCoverage || 0) * 100);
  const wagesPartial = wageCoveragePct > 0 && wageCoveragePct < 99;
  const wagesMissing = (t.totalHours || 0) > 0 && (t.totalWages || 0) === 0;

  if (employeeHours.length === 0) {
    return (
      <div className="analytics-empty">
        <div className="analytics-empty-icon">{'\u{1F552}'}</div>
        <div className="analytics-empty-text">No time entries for this period</div>
        <div className="analytics-empty-subtext">Once staff clock in via Toast, their hours appear here.</div>
      </div>
    );
  }

  return (
    <>
      {/* Labor KPI strip */}
      <div className="analytics-stats analytics-stats--labor">
        <div className="stat-card stat-card--accent">
          <div className="stat-card-label">Total Hours</div>
          <div className="stat-card-value">{fmtHrs(t.totalHours)}</div>
          <div className="stat-card-sub">{fmtHrs(t.totalRegularHours)} reg · {fmtHrs(t.totalOTHours)} OT</div>
        </div>
        <div className={`stat-card ${wagesMissing || wagesPartial ? 'is-warn' : ''}`}>
          <div className="stat-card-label">Labor Cost</div>
          <div className="stat-card-value currency">{fmt$(t.totalWages)}</div>
          {wagesMissing && <div className="stat-card-sub">No wage data resolved</div>}
          {wagesPartial && <div className="stat-card-sub">{wageCoveragePct}% wage coverage</div>}
        </div>
        <div className={`stat-card ${t.laborCostPct > 35 ? 'is-warn' : ''}`}>
          <div className="stat-card-label">Labor % of Sales</div>
          <div className="stat-card-value">{fmtPct(t.laborCostPct)}</div>
          <div className="stat-card-sub">{fmt$Compact(t.totalWages)} / {fmt$Compact(t.totalSales)}</div>
        </div>
        <div className="stat-card stat-card--accent">
          <div className="stat-card-label">Covers / Lab-hr</div>
          <div className="stat-card-value">{fmtRatio(t.coversPerLaborHour)}</div>
          <div className="stat-card-sub">{fmtN(data.totals?.totalGuests || 0)} covers</div>
        </div>
        <div className="stat-card stat-card--accent">
          <div className="stat-card-label">Revenue / Lab-hr</div>
          <div className="stat-card-value currency">{fmt$Compact(t.revenuePerLaborHour)}</div>
          <div className="stat-card-sub">{fmt$Compact(t.totalSales)} sales</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Peak Crew</div>
          <div className="stat-card-value">{peak || '—'}</div>
          <div className="stat-card-sub">{peak > 0 ? `@ ${peakHourLabel}` : 'No staffing data'}</div>
        </div>
      </div>

      {/* REG / OT split bar */}
      <div className="analytics-table-container">
        <div className="analytics-section-head">
          <span className="analytics-section-eyebrow">Regular vs Overtime</span>
          <span className="analytics-section-sub">Premium hours and the dollar weight</span>
        </div>
        <OTSplit
          regHours={t.totalRegularHours}
          otHours={t.totalOTHours}
          totalWages={t.totalWages}
          employeeHours={employeeHours}
        />
      </div>

      {/* Hourly coverage swimlane */}
      <div className="analytics-table-container">
        <div className="analytics-section-head">
          <span className="analytics-section-eyebrow">Coverage by hour, by role</span>
          <span className="analytics-section-sub">Concurrent staff · 30-min resolution · ALL row = total</span>
        </div>
        {roles.length === 0 ? (
          <div className="analytics-empty"><div className="analytics-empty-subtext">No role data</div></div>
        ) : (
          <div className="analytics-chart-wrap">
            <SwimlaneChart roles={roles} overall={overall} peak={peak} />
          </div>
        )}
      </div>

      {/* Role rollup — full width */}
      <div className="analytics-table-container">
        <div className="analytics-section-head">
          <span className="analytics-section-eyebrow">Role rollup</span>
          <span className="analytics-section-sub">{roles.length} roles · sorted by hours · cost share inline</span>
        </div>
        <RoleRollup roles={roles} maxHours={maxHours} totalCost={t.totalWages} />
      </div>

      {/* Attendance — scheduled vs actual */}
      {data.attendance && data.attendance.hasSchedule && (
        <>
          <div className="analytics-stats analytics-stats--attendance">
            <div className="stat-card stat-card--accent">
              <div className="stat-card-label">Scheduled</div>
              <div className="stat-card-value">{fmtHrs(data.attendance.totalSched)}</div>
              <div className="stat-card-sub">hours on the books</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-label">Actual</div>
              <div className="stat-card-value">{fmtHrs(data.attendance.totalActual)}</div>
              <div className="stat-card-sub">clocked-in hours</div>
            </div>
            <div className={`stat-card ${data.attendance.adherence !== null && data.attendance.adherence < 0.85 ? 'is-warn' : ''}`}>
              <div className="stat-card-label">Adherence</div>
              <div className="stat-card-value">
                {data.attendance.adherence === null ? '—' : `${Math.round(data.attendance.adherence * 100)}%`}
              </div>
              <div className="stat-card-sub">of scheduled hours worked</div>
            </div>
            <div className={`stat-card ${data.attendance.noShows > 0 ? 'is-bad' : ''}`}>
              <div className="stat-card-label">No-shows</div>
              <div className="stat-card-value">{data.attendance.noShows}</div>
              <div className="stat-card-sub">scheduled, didn't clock</div>
            </div>
            <div className={`stat-card ${data.attendance.unscheduled > 0 ? 'is-warn' : ''}`}>
              <div className="stat-card-label">Unscheduled</div>
              <div className="stat-card-value">{data.attendance.unscheduled}</div>
              <div className="stat-card-sub">clocked without a shift</div>
            </div>
          </div>

          <div className="analytics-two-col">
            <div className="analytics-table-container">
              <div className="analytics-section-head">
                <span className="analytics-section-eyebrow">Attendance by role</span>
                <span className="analytics-section-sub">S = scheduled · A = actual</span>
              </div>
              <AttendanceRoleChart roles={data.attendance.roles} />
            </div>
            <div className="analytics-table-container">
              <div className="analytics-section-head">
                <span className="analytics-section-eyebrow">Attendance flags</span>
                <span className="analytics-section-sub">No-shows, unscheduled, and ≥15% variance</span>
              </div>
              <AttendanceVariance employees={data.attendance.employees} />
            </div>
          </div>
        </>
      )}

      {data.attendance && !data.attendance.hasSchedule && (
        <div className="analytics-table-container">
          <div className="analytics-empty">
            <div className="analytics-empty-icon">{'\u{1F4C5}'}</div>
            <div className="analytics-empty-text">No scheduled shifts for this period</div>
            <div className="analytics-empty-subtext">Attendance chart compares scheduled hours to clocked-in hours — both are needed.</div>
          </div>
        </div>
      )}

      {/* Top earners */}
      <div className="analytics-table-container">
        <div className="analytics-section-head">
          <span className="analytics-section-eyebrow">Top earners</span>
          <span className="analytics-section-sub">By labor cost · top 8</span>
        </div>
        <TopEarners employeeHours={employeeHours} />
      </div>

      {/* Punch timeline — Gantt-style spans of each employee's clock-in/out windows */}
      <div className="analytics-table-container">
        <div className="analytics-section-head">
          <span className="analytics-section-eyebrow">Punch timeline</span>
          <span className="analytics-section-sub">Clock-in → clock-out spans · sorted by start time · dashed = active shift</span>
        </div>
        <div className="analytics-chart-wrap">
          <PunchTimeline employees={groupShiftsByEmployee(employeeHours)} />
        </div>
      </div>

      {/* Detail table (existing behavior, retained) */}
      <div className="analytics-table-container">
        <div className="analytics-section-head">
          <span className="analytics-section-eyebrow">Detail · per-shift</span>
          <span className="analytics-section-sub">{employeeHours.length} time {employeeHours.length === 1 ? 'entry' : 'entries'}</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="analytics-table" style={{ minWidth: 700 }}>
            <thead><tr><th>Employee</th><th>Position</th><th>In</th><th>Out</th><th className="right">Reg</th><th className="right">OT</th><th className="right">Total</th><th className="right">Rate</th><th className="right">Pay</th></tr></thead>
            <tbody>{employeeHours.map((eh, i) => (
              <tr key={i}><td style={{ fontWeight: 600 }}>{eh.employeeName}</td><td><span className={`position-badge ${eh.tpiEnabled ? 'tipped' : ''}`}>{eh.jobTitle}</span></td>
                <td>{fmtTime(eh.clockIn)}</td><td>{eh.clockOut ? fmtTime(eh.clockOut) : <span className="badge-active">Active</span>}</td>
                <td className="right">{fmtHrs(eh.regularHours)}</td><td className="right">{eh.overtimeHours > 0 ? <span className="ot-highlight">{fmtHrs(eh.overtimeHours)}</span> : '0.00'}</td>
                <td className="right" style={{ fontWeight: 700 }}>{fmtHrs(eh.totalHours)}</td><td className="right">{fmt$(eh.hourlyWage)}/hr</td><td className="right currency" style={{ fontWeight: 700 }}>{fmt$(eh.totalWage)}</td></tr>
            ))}</tbody>
            <tfoot><tr className="totals-row"><td colSpan={4}>Totals</td><td className="right">{fmtHrs(t.totalRegularHours)}</td><td className="right">{fmtHrs(t.totalOTHours)}</td>
              <td className="right" style={{ fontWeight: 700 }}>{fmtHrs(t.totalHours)}</td><td></td><td className="right currency" style={{ fontWeight: 700 }}>{fmt$(t.totalWages)}</td></tr></tfoot>
          </table>
        </div>
      </div>
    </>
  );
};

const TipsTab = ({ data }) => {
  const { employeeTips, laborTotals: t } = data;
  return (
    <div className="analytics-table-container">
      <h3 className="analytics-table-title">Tips by Employee</h3>
      {employeeTips.length === 0 ? <div className="analytics-empty"><div className="analytics-empty-subtext">No tip data</div></div> : (
        <div style={{ overflowX: 'auto' }}>
          <table className="analytics-table" style={{ minWidth: 700 }}>
            <thead><tr><th>Employee</th><th>Position</th><th className="right">Cash</th><th className="right">CC</th><th className="right">Declared</th><th className="right">Auto Grat</th><th className="right">Total</th><th className="right">Hours</th><th className="right">$/Hr</th></tr></thead>
            <tbody>{employeeTips.map((et, i) => (
              <tr key={i}><td style={{ fontWeight: 600 }}>{et.employeeName}</td><td><span className={`position-badge ${et.tpiEnabled ? 'tipped' : ''}`}>{et.jobTitle}</span></td>
                <td className="right">{fmt$(et.cashTips)}</td><td className="right">{fmt$(et.nonCashTips)}</td><td className="right">{fmt$(et.declaredCashTips)}</td><td className="right">{fmt$(et.autoGratuity)}</td>
                <td className="right currency" style={{ fontWeight: 700 }}>{fmt$(et.totalTips)}</td><td className="right">{fmtHrs(et.totalHours)}</td><td className="right">{fmt$(et.tipsPerHour)}</td></tr>
            ))}</tbody>
            <tfoot><tr className="totals-row"><td colSpan={2}>Totals</td><td className="right">{fmt$(t.totalCashTips)}</td><td className="right">{fmt$(t.totalCCTips)}</td>
              <td className="right">{fmt$(t.totalDeclaredTips)}</td><td className="right">{fmt$(t.totalAutoGrat)}</td><td className="right currency" style={{ fontWeight: 700 }}>{fmt$(t.totalAllTips)}</td>
              <td className="right">{fmtHrs(t.totalHours)}</td><td></td></tr></tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

const ServersTab = ({ data }) => {
  const { serverSales, laborTotals: t } = data;
  return (
    <div className="analytics-table-container">
      <h3 className="analytics-table-title">Server Sales & Tips</h3>
      {serverSales.length === 0 ? <div className="analytics-empty"><div className="analytics-empty-subtext">No data</div></div> : (
        <table className="analytics-table">
          <thead><tr><th>Server</th><th className="right">Orders</th><th className="right">Sales</th><th className="right">Tips</th><th className="right">Tip %</th><th className="right">Avg Check</th></tr></thead>
          <tbody>{serverSales.map((ss, i) => (
            <tr key={i}><td style={{ fontWeight: 600 }}>{ss.serverName}</td><td className="right">{fmtN(ss.orderCount)}</td><td className="right currency">{fmt$(ss.totalSales)}</td>
              <td className="right currency">{fmt$(ss.tipsReceived)}</td><td className="right">{fmtPct(ss.tipPercent)}</td><td className="right">{fmt$(ss.avgCheckSize)}</td></tr>
          ))}</tbody>
          <tfoot><tr className="totals-row"><td>Totals</td><td className="right">{fmtN(t.totalOrders)}</td><td className="right currency">{fmt$(t.totalSales)}</td>
            <td className="right currency">{fmt$(t.totalTipsFromOrders)}</td><td className="right">{fmtPct(t.overallTipPercent)}</td><td></td></tr></tfoot>
        </table>
      )}
    </div>
  );
};

// ============ Inventory tab ============

const InventoryTab = ({ data }) => {
  const inv = data.inventory || {};
  const usage = inv.usage || {
    ingredients: [],
    itemsWithRecipe: 0,
    itemsWithoutRecipe: 0,
    menuItemsSeen: 0,
    recipeCoverage: 0,
    untrackedSellers: [],
  };
  const items = inv.items || [];

  return (
    <>
      <div className="analytics-stats analytics-stats--inventory">
        <div className={`stat-card stat-card--accent ${inv.outOfStockCount > 0 ? 'is-bad' : ''}`}>
          <div className="stat-card-label">Out of Stock</div>
          <div className="stat-card-value">{fmtN(inv.outOfStockCount || 0)}</div>
          <div className="stat-card-sub">86’d in Toast</div>
        </div>
        <div className={`stat-card ${inv.lowStockCount > 0 ? 'is-warn' : ''}`}>
          <div className="stat-card-label">Limited Quantity</div>
          <div className="stat-card-value">{fmtN(inv.lowStockCount || 0)}</div>
          <div className="stat-card-sub">{fmtN(inv.totalQuantityRemaining || 0)} units remaining</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Recipe Coverage</div>
          <div className="stat-card-value">{Math.round((usage.recipeCoverage || 0) * 100)}%</div>
          <div className="stat-card-sub">
            {fmtN(usage.itemsWithRecipe)} / {fmtN(usage.menuItemsSeen)} items with recipes
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Ingredients Used</div>
          <div className="stat-card-value">{fmtN(usage.ingredients.length)}</div>
          <div className="stat-card-sub">distinct ingredients consumed this period</div>
        </div>
      </div>

      {/* Stock status table — only render when Toast actually flagged items */}
      {items.length > 0 && (
        <div className="analytics-table-container">
          <h3 className="analytics-table-title">Stock Status</h3>
          <p className="analytics-table-subtitle">
            Items Toast currently lists as out-of-stock or limited.
            {inv.invalidCount > 0 && (
              <span className="inventory-status inventory-status-warn" style={{ marginLeft: 8 }}>
                {fmtN(inv.invalidCount)} invalid GUID{inv.invalidCount === 1 ? '' : 's'}
              </span>
            )}
          </p>
          <table className="analytics-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Item</th>
                <th className="right">Status</th>
                <th className="right">Qty Remaining</th>
              </tr>
            </thead>
            <tbody>
              {items.slice(0, 100).map((item, i) => {
                const isOut = item.status === 'OUT_OF_STOCK';
                const label = isOut ? 'Out' : 'Limited';
                const statusClass = isOut ? 'inventory-status-bad' : 'inventory-status-warn';
                const displayName = item.name
                  || (item.guid ? `Item ${item.guid.slice(0, 8)}…` : 'Unknown item');
                return (
                  <tr key={item.guid || i}>
                    <td><span className={`rank-badge ${i < 3 ? 'top-3' : ''}`}>{i + 1}</span></td>
                    <td style={{ fontWeight: 600 }}>
                      {displayName}
                      {item.isInvalid && (
                        <span className="inventory-status inventory-status-warn" style={{ marginLeft: 8 }}>
                          invalid GUID
                        </span>
                      )}
                    </td>
                    <td className="right"><span className={`inventory-status ${statusClass}`}>{label}</span></td>
                    <td className="right">
                      {Number.isFinite(item.quantity) ? fmtN(item.quantity) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="analytics-table-container">
        <h3 className="analytics-table-title">Ingredients Consumed (Recipe × Units Sold)</h3>
        <p className="analytics-table-subtitle">
          Estimated quantity used this period, rolled up from the recipes on items that sold.
          Cost is only shown for ingredients with a unit cost in the menu — no defaults are applied.
        </p>
        {usage.ingredients.length === 0 ? (
          <div className="analytics-empty">
            <div className="analytics-empty-icon">{'\u{1F37D}'}</div>
            <div className="analytics-empty-text">No recipe data</div>
            <div className="analytics-empty-subtext">
              None of the items sold this period have recipes attached.
              Add ingredient lists to menu items to surface ingredient burn here.
            </div>
          </div>
        ) : (
          <table className="analytics-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Ingredient</th>
                <th className="right">Quantity used</th>
                <th className="right">Unit</th>
                <th className="right">Cost</th>
                <th className="right">Menu items</th>
              </tr>
            </thead>
            <tbody>
              {usage.ingredients.slice(0, 50).map((ing, i) => (
                <tr key={`${ing.name}__${ing.unit}`}>
                  <td><span className={`rank-badge ${i < 3 ? 'top-3' : ''}`}>{i + 1}</span></td>
                  <td style={{ fontWeight: 600 }}>{ing.name}</td>
                  <td className="right">{fmtN(ing.totalQty)}</td>
                  <td className="right">{ing.unit || '—'}</td>
                  <td className="right">
                    {ing.hasUnitCost && ing.totalCost > 0 ? fmt$(ing.totalCost) : '—'}
                  </td>
                  <td className="right">{fmtN(ing.itemsUsing)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="totals-row">
                <td colSpan="5">
                  {usage.ingredients.length > 50
                    ? `Top 50 of ${usage.ingredients.length} ingredients`
                    : `${usage.ingredients.length} ingredient${usage.ingredients.length === 1 ? '' : 's'}`}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {usage.untrackedSellers.length > 0 && (
        <div className="analytics-table-container">
          <h3 className="analytics-table-title">Items Without Recipes</h3>
          <p className="analytics-table-subtitle">
            These items sold this period but have no ingredient list, so their consumption isn't reflected above.
            Add a recipe to track them.
          </p>
          <table className="analytics-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Item</th>
                <th className="right">Units sold</th>
                <th className="right">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {usage.untrackedSellers.slice(0, 25).map((item, i) => (
                <tr key={item.name}>
                  <td><span className={`rank-badge ${i < 3 ? 'top-3' : ''}`}>{i + 1}</span></td>
                  <td style={{ fontWeight: 600 }}>{item.name}</td>
                  <td className="right">{fmtN(item.quantity)}</td>
                  <td className="right">{fmt$(item.revenue || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

// ============ Main Component ============

const Analytics = () => {
  const { dataSource } = useAppSettings();
  const { data, loading, error, fetchData } = useUnifiedAnalytics();
  const [viewMode, setViewMode] = useState('daily');
  const [activeTab, setActiveTab] = useState('overview');

  // Daily state
  const [dailyDate, setDailyDate] = useState(todayYMD());

  // Weekly state
  const [weekStart, setWeekStart] = useState(() => getMonday(toISO(todayYMD())));

  // Annual state
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [isYTD, setIsYTD] = useState(true);

  const doFetch = () => {
    if (viewMode === 'daily') {
      fetchData({ businessDate: dailyDate });
    } else if (viewMode === 'weekly') {
      const sun = getSunday(weekStart);
      fetchData({ startDate: toISO(weekStart) + 'T00:00:00.000Z', endDate: toISO(sun) + 'T23:59:59.999Z' });
    } else {
      const start = `${selectedYear}-01-01T00:00:00.000Z`;
      const end = isYTD && selectedYear === currentYear
        ? new Date().toISOString()
        : `${selectedYear}-12-31T23:59:59.999Z`;
      fetchData({ startDate: start, endDate: end });
    }
  };

  useEffect(() => { doFetch(); }, [viewMode, dailyDate, weekStart, selectedYear, isYTD, dataSource]);

  const years = [];
  for (let y = currentYear; y >= currentYear - 3; y--) years.push(y);

  return (
    <div className="analytics-page">
      <header className="analytics-header">
        <div className="analytics-header-top">
          <h1>Analytics</h1>
          <div className="view-toggle">
            {VIEW_MODES.map(v => (
              <button key={v.id} className={`view-toggle-btn ${viewMode === v.id ? 'active' : ''}`}
                onClick={() => { setViewMode(v.id); setActiveTab('overview'); }}>
                {v.label}
              </button>
            ))}
          </div>
        </div>
        <div className="analytics-controls">
          {viewMode === 'daily' && (
            <div className="day-selector">
              <button className="week-nav-btn" aria-label="Previous day"
                onClick={() => setDailyDate(shiftDay(dailyDate, -1))}>{'◀'}</button>
              <label className="day-picker-trigger">
                <span className="week-label day-label">{formatDayLabel(dailyDate)}</span>
                <input
                  type="date"
                  className="day-picker-input"
                  value={toISO(dailyDate)}
                  max={toISO(todayYMD())}
                  onChange={(e) => e.target.value && setDailyDate(fromISO(e.target.value))}
                />
              </label>
              <button className="week-nav-btn" aria-label="Next day"
                disabled={dailyDate >= todayYMD()}
                onClick={() => setDailyDate(shiftDay(dailyDate, 1))}>{'▶'}</button>
              <button
                className={`day-today-btn ${dailyDate === todayYMD() ? 'is-current' : ''}`}
                onClick={() => setDailyDate(todayYMD())}
                disabled={dailyDate === todayYMD()}
                aria-label="Jump to today"
              >Today</button>
            </div>
          )}
          {viewMode === 'weekly' && (
            <div className="week-selector">
              <button className="week-nav-btn" onClick={() => setWeekStart(shiftWeek(weekStart, -1))}>{'\u25C0'}</button>
              <span className="week-label">{formatDateRange(weekStart, getSunday(weekStart))}</span>
              <button className="week-nav-btn" onClick={() => setWeekStart(shiftWeek(weekStart, 1))}>{'\u25B6'}</button>
            </div>
          )}
          {viewMode === 'annual' && (
            <div className="annual-selector">
              <select className="year-select" value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <label className="ytd-toggle">
                <input type="checkbox" checked={isYTD} onChange={(e) => setIsYTD(e.target.checked)} />
                <span>YTD</span>
              </label>
            </div>
          )}
          <button className="analytics-refresh-btn" onClick={doFetch} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          <span className={`analytics-source-badge ${dataSource === 'toast' ? 'badge-live' : 'badge-sim'}`}>
            {dataSource === 'toast' ? 'LIVE' : 'SIM'}
          </span>
        </div>
      </header>

      <nav className="analytics-nav">
        <div className="analytics-tabs">
          {TABS.map(tab => (
            <button key={tab.id} className={`analytics-tab ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {error && <div className="analytics-error">{error}</div>}

      {loading ? (
        <div className="analytics-loading"><div className="analytics-spinner" /><span>Loading analytics...</span></div>
      ) : !data ? (
        <div className="analytics-empty"><div className="analytics-empty-icon">{'\u{1F4CA}'}</div><div className="analytics-empty-text">No data available</div></div>
      ) : data.overview?.orderCount === 0 && data.employeeHours?.length === 0 ? (
        <div className="analytics-empty"><div className="analytics-empty-icon">{'\u{1F4ED}'}</div><div className="analytics-empty-text">No data found for this period</div></div>
      ) : (
        <main>
          {activeTab === 'overview' && <OverviewTab data={data} viewMode={viewMode} />}
          {activeTab === 'sales' && <SalesTab data={data} />}
          {activeTab === 'hourly' && <HourlyTab data={data} viewMode={viewMode} />}
          {activeTab === 'items' && <TopItemsTab data={data} />}
          {activeTab === 'payments' && <PaymentsTab data={data} />}
          {activeTab === 'labor' && <LaborTab data={data} />}
          {activeTab === 'tips' && <TipsTab data={data} />}
          {activeTab === 'servers' && <ServersTab data={data} />}
          {activeTab === 'inventory' && <InventoryTab data={data} />}
        </main>
      )}
    </div>
  );
};

export default Analytics;
